import React, { useState, useEffect } from "react";
import { Modal, Input, Select, Icon, Row, Col, Checkbox } from "antd";
import TruncatedText from "../TruncatedText";
import PHONE_COUNTRIES_BY_DIAL_CODE from "../../../helpers/phoneCountriesByDialCode";
import PHONE_FORMATS_BY_ISO from "../../../helpers/phoneFormatsByIso";

const { Option } = Select;
const { TextArea } = Input;

const DEFAULT_COUNTRY_ISO = "us";
const DEFAULT_MAX_PHONE_DIGITS = 10;

/** One option per dial code (e.g. +1 for US/CA/etc.) — prefer default ISO when shared. */
const UNIQUE_COUNTRIES_BY_DIAL_CODE = (() => {
  const byDialCode = new Map();
  for (const country of PHONE_COUNTRIES_BY_DIAL_CODE) {
    const existing = byDialCode.get(country.dialCode);
    if (!existing || country.iso2 === DEFAULT_COUNTRY_ISO) {
      byDialCode.set(country.dialCode, country);
    }
  }
  return Array.from(byDialCode.values());
})();

const getCountryByIso = (iso) =>
  UNIQUE_COUNTRIES_BY_DIAL_CODE.find((c) => c.iso2 === iso) ||
  UNIQUE_COUNTRIES_BY_DIAL_CODE.find((c) => c.iso2 === DEFAULT_COUNTRY_ISO);

const getMaxNationalDigits = (iso) =>
  PHONE_FORMATS_BY_ISO[iso]?.maxNationalDigits ||
  PHONE_FORMATS_BY_ISO[DEFAULT_COUNTRY_ISO]?.maxNationalDigits ||
  DEFAULT_MAX_PHONE_DIGITS;

/** Digits only — strips -, ., spaces, letters, and other special chars. */
const digitsOnly = (value = "", maxLength) => {
  const digits = String(value).replace(/\D/g, "");
  return typeof maxLength === "number" ? digits.slice(0, maxLength) : digits;
};

/** Allow letters/digits/spaces for country search; strip -, . and other specials. */
const sanitizeCountrySearch = (value = "") =>
  String(value).replace(/[^a-zA-Z0-9\s]/g, "").slice(0, 40);

/** Apply react-phone-input-2 style mask (`.` = digit) to a digit string. */
const applyPhoneFormatMask = (digitString = "", format) => {
  if (!digitString) return "";
  if (!format) return digitString;

  let formatted = "";
  let i = 0;
  for (const char of format) {
    if (i >= digitString.length) break;
    if (char === ".") {
      formatted += digitString[i];
      i += 1;
    } else {
      formatted += char;
    }
  }
  if (formatted.includes("(") && !formatted.includes(")")) {
    formatted += ")";
  }
  return formatted;
};

/** Format national digits; strip dial-code portion from the full mask. */
const formatPhoneByCountry = (digits = "", iso = DEFAULT_COUNTRY_ISO) => {
  const country = getCountryByIso(iso);
  const dialCode = country?.dialCode || "1";
  const national = digitsOnly(digits, getMaxNationalDigits(iso));
  if (!national) return "";

  const format = PHONE_FORMATS_BY_ISO[iso]?.format;
  if (!format) return national;

  return applyPhoneFormatMask(`${dialCode}${national}`, format).replace(
    new RegExp(`^\\+?${dialCode}\\s?`),
    ""
  );
};

const phonePlaceholderByCountry = (iso = DEFAULT_COUNTRY_ISO) => {
  const maxDigits = getMaxNationalDigits(iso);
  if (!PHONE_FORMATS_BY_ISO[iso]?.format) {
    return "0".repeat(Math.min(maxDigits, 10));
  }
  return formatPhoneByCountry("0".repeat(maxDigits), iso);
};

const phoneInputMaxLength = (iso = DEFAULT_COUNTRY_ISO) => {
  const maxDigits = getMaxNationalDigits(iso);
  return Math.max(formatPhoneByCountry("0".repeat(maxDigits), iso).length, maxDigits);
};

const parseEmergencyResponseNumber = (value, countries) => {
  const raw = String(value || "").replace(/\D/g, "");
  if (!raw) {
    return { countryIso: DEFAULT_COUNTRY_ISO, phoneDigits: "" };
  }

  const byDialLength = [...countries].sort(
    (a, b) => b.dialCode.length - a.dialCode.length || a.name.localeCompare(b.name)
  );

  for (const country of byDialLength) {
    if (raw.startsWith(country.dialCode)) {
      const preferred =
        countries.find(
          (c) => c.dialCode === country.dialCode && c.iso2 === DEFAULT_COUNTRY_ISO
        ) || country;
      return {
        countryIso: preferred.iso2,
        phoneDigits: raw.slice(country.dialCode.length),
      };
    }
  }

  return { countryIso: DEFAULT_COUNTRY_ISO, phoneDigits: raw };
};

function UN_Number(props) {
  const {
    visible,
    onCancel,
    onSave,
    unSource = [],
    unNumber,
    selectedUN,
    setSelectedUN,
    hazmatClass,
    emergencyResponseNumber,
    description,
    setHazmatClass,
    setEmergencyResponseNumber,
    setDescription,
    isEditing = false,
    pkgGroup,
    setPkgGroup,
    isPlacard,
    setIsPlacard,
    isEditMode = true,
    reqLoading
  } = props;
  const [validationError, setValidationError] = useState([]);
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [phoneDigits, setPhoneDigits] = useState("");

  useEffect(() => {
    if (!visible) return;
    const parsed = parseEmergencyResponseNumber(
      emergencyResponseNumber,
      UNIQUE_COUNTRIES_BY_DIAL_CODE
    );
    // Keep parsed digits as stored; max length is enforced only on type/paste/country change.
    setCountryIso(parsed.countryIso);
    setPhoneDigits(parsed.phoneDigits || "");
    // Parse only when the modal opens so country selection is not reset on typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const syncEmergencyResponseNumber = (nextIso, nextDigits) => {
    const country = getCountryByIso(nextIso);
    const dialCode = country?.dialCode || "1";
    const digits = digitsOnly(nextDigits, getMaxNationalDigits(nextIso));
    setEmergencyResponseNumber(digits ? `${dialCode}${digits}` : "");
  };

  const handleCountryChange = (iso) => {
    const truncated = digitsOnly(phoneDigits, getMaxNationalDigits(iso));
    setCountryIso(iso);
    setPhoneDigits(truncated);
    syncEmergencyResponseNumber(iso, truncated);
  };

  const handleCountryInputKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // Block -, ., and other special chars in the dropdown search box.
    if (e.key.length === 1 && /[^a-zA-Z0-9\s]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePhoneNumberChange = (e) => {
    const digits = digitsOnly(e.target.value, getMaxNationalDigits(countryIso));
    setPhoneDigits(digits);
    syncEmergencyResponseNumber(countryIso, digits);
  };

  const handlePhoneNumberKeyPress = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // Digits only; formatting chars are added by us, not typed.
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSave = () => {
    if (!selectedUN) {
      setValidationError((prev) => ({ ...prev, selectedUN: "Please select UN Number" }));
      return;
    }
    if (!hazmatClass) {
      setValidationError((prev) => ({ ...prev, hazmatClass: "Please select Hazmat Class" }));
      return;
    }
    // if (!pkgGroup) {
    //   setValidationError((prev) => ({ ...prev, pkgGroup: "Please select Packing Group" }));
    //   return;
    // }
    onSave({
      un_number: selectedUN,
      hazmat_class: hazmatClass,
      packing_group: pkgGroup,
      is_placard_required: isPlacard === true || isPlacard === "true" ? 'true' : 'false',
      emergency_response_number: emergencyResponseNumber,
      description,
    });
    setValidationError("");
  };

  const handleChangeUnDetails = (value) => {
      setSelectedUN(value);

      const filteredData = unSource.find(
          (val) => val.un_number === value
      );
      setHazmatClass(filteredData?.hazard_class);
      setPkgGroup(filteredData?.packing_group);
      setDescription(filteredData?.description)
  };

  const uniqueHazmatClasses = Array.from(
    new Set(
      unSource
        .map((un) => un.hazard_class)
        .concat(hazmatClass ? [hazmatClass] : [])
        .filter((val) => val !== undefined && val !== null && val !== "")
    )
  );

  const uniquePkgGroups = Array.from(
    new Set(
      unSource
        .map((un) => un.packing_group)
        .concat(pkgGroup ? [pkgGroup] : [])
        .filter((val) => val !== undefined && val !== null && val !== "")
    )
  );

  return (
    <Modal
      title={isEditing ? "Edit UN Number" : "Add UN Number"}
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      width={700}
      destroyOnClose
      confirmLoading={!isEditMode ? reqLoading : false}
      // footer={isEditMode ? undefined : null}
    >
      <Row gutter={16} type="flex">
        <Col style={{ flex: 1 }} span={8}>
          <div style={{ marginBottom: 16 }}>
            <label>
              UN Number <span style={{ color: "red" }}>*</span>
            </label>

            <Select
              showSearch
              allowClear
              value={selectedUN || undefined}
              placeholder="Select UN Number"
              style={{ width: "100%", marginTop: 4 }}
              onChange={value => {
                handleChangeUnDetails(value);
                setValidationError("");
              }}
              suffixIcon={<Icon style={{ color: "#576EC1" }} type="caret-down" />}
              filterOption={(input, option) =>
                (option.props["data-search"] || "").toLowerCase().includes(input.toLowerCase())
              }
              // disabled={!isEditMode}
            >
              {unSource.map(un => (
                <Option
                  key={un.un_number}
                  value={un.un_number}
                  data-search={`${un.un_number}`}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {un.placard_logo && (
                      <img
                        src={un.placard_logo}
                        alt=""
                        style={{
                          width: 20,
                          height: 20,
                        }}
                      />
                    )}

                    <TruncatedText
                      text={`${un.un_number}`}
                      style={{
                        color: "#1A354D",
                        fontWeight: 500,
                      }}
                    />
                  </div>
                </Option>
              ))}
            </Select>

            {validationError && (
              <div
                style={{
                  color: "#F5222D",
                  marginTop: 4,
                  fontSize: 12,
                }}
              >
                {validationError?.selectedUN}
              </div>
            )}
          </div>
        </Col>

       <Col style={{ flex: 1 }} span={8}>
          <div style={{ marginBottom: 16 }}>
            <label>Hazmat Class</label>
            <Select
              showSearch
              value={hazmatClass || undefined}
              placeholder="Select Hazmat Class"
              style={{ width: "100%", marginTop: 4 }}
              onChange={value => {
                setHazmatClass(value || "");
              }}
              suffixIcon={<Icon style={{ color: "#576EC1" }} type="caret-down" />}
              filterOption={(input, option) =>
                String(option.props.children || "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {uniqueHazmatClasses.map(hClass => (
                <Option key={hClass} value={hClass}>
                  {String(hClass)}
                </Option>
              ))}
            </Select>
            {validationError && (
              <div
                style={{
                  color: "#F5222D",
                  marginTop: 4,
                  fontSize: 12,
                }}
              >
                {validationError.hazmatClass}
              </div>
            )}
          </div>
        </Col>

         <Col style={{ flex: 1 }} span={8}>
          <div style={{ marginBottom: 16 }}>
            <label>Packing Group
            </label>
            <Select
              showSearch
              value={pkgGroup || undefined}
              placeholder="Select Packing Group"
              style={{ width: "100%", marginTop: 4 }}
              onChange={value => {
                setPkgGroup(value || "");
              }}
              suffixIcon={<Icon style={{ color: "#576EC1" }} type="caret-down" />}
              filterOption={(input, option) =>
                String(option.props.children || "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {uniquePkgGroups.map(pGroup => (
                <Option key={pGroup} value={pGroup}>
                  {String(pGroup)}
                </Option>
              ))}
            </Select>
              {/* {validationError && (
              <div
                style={{
                  color: "#F5222D",
                  marginTop: 4,
                  fontSize: 12,
                }}
              >
                {validationError.pkgGroup}
              </div>
            )} */}
          </div>
        </Col>
      </Row>

      <Row gutter={16} type="flex" align="bottom">
        <Col span={5}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                marginBottom: 5,
                fontWeight: 500,
              }}
            >
              Country Code
            </label>
            <Select
              showSearch
              value={countryIso}
              placeholder="Code"
              style={{ width: "100%" }}
              onChange={handleCountryChange}
              onInputKeyDown={handleCountryInputKeyDown}
              suffixIcon={<Icon style={{ color: "#576EC1" }} type="caret-down" />}
              filterOption={(input, option) =>
                (option.props["data-search"] || "")
                  .toLowerCase()
                  .includes(sanitizeCountrySearch(input).toLowerCase())
              }
            >
              {UNIQUE_COUNTRIES_BY_DIAL_CODE.map((country) => (
                <Option
                  key={country.dialCode}
                  value={country.iso2}
                  data-search={`${country.name} ${country.dialCode} +${country.dialCode} ${country.iso2}`}
                >
                  {`+${country.dialCode}`}
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col span={11}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                marginBottom: 5,
                fontWeight: 500,
              }}
            >
              Emergency Response Number
            </label>
            <Input
              value={formatPhoneByCountry(phoneDigits, countryIso)}
              onChange={handlePhoneNumberChange}
              onKeyPress={handlePhoneNumberKeyPress}
              onPaste={(e) => {
                e.preventDefault();
                const digits = digitsOnly(
                  (e.clipboardData || window.clipboardData).getData("text"),
                  getMaxNationalDigits(countryIso)
                );
                setPhoneDigits(digits);
                syncEmergencyResponseNumber(countryIso, digits);
              }}
              placeholder={phonePlaceholderByCountry(countryIso)}
              style={{ height: 32 }}
              inputMode="numeric"
              maxLength={phoneInputMaxLength(countryIso)}
            />
          </div>
        </Col>

        <Col span={8}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", height: 32, marginTop: 22 }}>
              <Checkbox
                checked={isPlacard}
                onChange={(e) => {
                  setIsPlacard(!isPlacard);
                }}
              >
                <span style={{ marginLeft: 8 }}>Requires Placard</span>
              </Checkbox>
            </label>
          </div>
        </Col>
      </Row>

      <div>
        <label>Description</label>
        <TextArea
          rows={4}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Enter Description"
          style={{ marginTop: 4 }}
        />
      </div>
    </Modal>
  );
}

export default UN_Number;
